// @flow
import { has } from "../../utils/helpers/objects";

import {
  DateUtil,
  DateTimeUtil,
  TimeUtil,
  TimestampUtil,
} from "../../utils/datetime/DateTimeUtil";

import StringAttributeModel from "./StringAttributeModel";

import ConstraintCollection from "./input-constraints/ConstraintCollection";
import DateTimeDateFormatConstraint from "./input-constraints/DateTimeDateFormatConstraint";
import DateTimeTimeFormatConstraint from "./input-constraints/DateTimeTimeFormatConstraint";
import DatetimeFormatConstraint from "./input-constraints/DatetimeFormatConstraint";
import DateBoundaryConstraint from "./input-constraints/DateBoundaryConstraint";

import {
  isIncludeTimeOffsetInDateTimes,
  ATTRIBUTE_WIDTH,
  DATETIME_OFFSET_FORMAT,
  ISO_DATE_FORMAT,
} from "../../constants";

import type {
  FormErrorAnchor,
  IConstraintModel,
  AttributeType,
  ModelOptions,
} from "../types";
import type { OffsetInfo } from "../../utils/datetime/DateTimeUtil";

/**
 */
class DatetimeAttributeModel extends StringAttributeModel {
  _dateInputFormat: ?string;
  _timeInputFormat: ?string;

  /**
   */
  constructor(
    attribute: Object,
    attributeContributions: Object,
    modelOptions?: ModelOptions,
  ) {
    super(attribute, attributeContributions, modelOptions);

    this.updateInitValue();
  }

  /**
   */
  static isApplicableModel(contributions: Object): boolean {
    return ["date", "time", "datetime", "timestamp"].includes(
      contributions.type,
    );
  }

  /**
   */
  get formatUtil():
    | typeof TimeUtil
    | typeof TimestampUtil
    | typeof DateTimeUtil
    | typeof DateUtil {
    switch (this.type) {
      case "time":
        return TimeUtil;
      case "timestamp":
        return TimestampUtil;
      case "datetime":
        return DateTimeUtil;
      default:
        return DateUtil;
    }
  }

  /**
   */
  getInitValue(): string | null {
    let value = this.data.value;
    if (value != null) {
      if (this.type === "time" && value.length === 5) {
        value = `${value}:00`;
      }

      // handle old datetime values, which contained ms
      if (this.type === "datetime" && value.includes(".")) {
        value = TimestampUtil.toFormat(value, DateTimeUtil.getIsoFormat());
      } else {
        value = this.formatUtil.toISO(value);
      }

      if (this.hasTime) {
        value = this.getTimeValue(value);
      }
    }

    return value;
  }

  /**
   */
  getTimeValue(value: string): string {
    let timeValue = value;
    if (!this.timeInputFormat.toLowerCase().includes("h")) {
      timeValue = this.formatUtil.setHour(value, 0);
    }
    if (!this.timeInputFormat.includes("m")) {
      timeValue = this.formatUtil.setMinute(value, 0);
    }
    if (!this.timeInputFormat.includes("s")) {
      timeValue = this.formatUtil.setSecond(value, 0);
    }
    if (this.type === "timestamp" && !this.timeInputFormat.includes("S")) {
      timeValue = this.formatUtil.setMilliseconds(value, 0);
    }

    if (isIncludeTimeOffsetInDateTimes()) {
      // https://github.com/date-fns/date-fns/issues/3579
      const oldOffset = this.formatUtil.toFormat(value, DATETIME_OFFSET_FORMAT);
      const newOffset = this.formatUtil.toFormat(
        timeValue,
        DATETIME_OFFSET_FORMAT,
      );
      timeValue = timeValue.replace(newOffset, oldOffset);
    }

    return timeValue;
  }

  /**
   * Set time in the correct value based on available elements in the time format
   */
  updateInitValue() {
    const value = this.getInitValue();
    if (value != null) {
      this._initvalue = value;
      this._inputvalue = this.getInitialInputValue(value);
      this._value = value;
    }
  }

  /**
   * Get initial user input value
   */
  getInitialInputValue(value?: string): string {
    if (!value) {
      return "";
    }

    let initValue = value;
    if (this.type === "time" && value.length === 5) {
      initValue = `${value}:00`;
    }

    return this.formatUtil.toFormat(initValue, this.inputFormat);
  }

  /**
   * Set type to date
   */
  get type(): string {
    return this.getContribution("type", "date");
  }

  /**
   * Returns the value as entered by the user. This can differ from the internal iso value that is stored
   */
  getInputValue(): string {
    return this._inputvalue ? this._inputvalue.toString() : "";
  }

  /**
   * Get input value
   */
  get inputvalue(): string {
    return this.getInputValue();
  }

  /**
   * Sets the input value to the value entered by the user
   */
  set inputvalue(value: string) {
    this.validate(value);

    this._inputvalue = value;

    this.value =
      value !== null && value !== ""
        ? this.formatUtil.toISO(value, this.inputFormat)
        : null;
  }

  /**
   * Get date format
   */
  get format(): string {
    const format = this.getContribution("format", "");
    return this.formatUtil.convertFormat(format);
  }

  /**
   */
  get hasDate(): boolean {
    return (
      this.type === "date" ||
      this.type === "datetime" ||
      this.type === "timestamp"
    );
  }

  /**
   */
  get hasTime(): boolean {
    return (
      this.type === "time" ||
      this.type === "datetime" ||
      this.type === "timestamp"
    );
  }

  /**
   */
  get formatLabel(): string {
    switch (this.type) {
      case "date":
        return this.dateInputFormat.toLowerCase();
      case "time":
        return this.timeInputFormat.toLowerCase();
      default:
        return this.format.toLowerCase();
    }
  }

  /**
   */
  get inputFormatWithoutOffset(): string {
    if (this.hasDate && this.hasTime) {
      return `${this.dateInputFormat} ${this.timeInputFormat}`.trim();
    }

    if (this.hasDate) {
      return this.dateInputFormat;
    }

    if (this.hasTime) {
      return this.timeInputFormat;
    }

    return "";
  }

  /**
   */
  get inputFormat(): string {
    if (
      isIncludeTimeOffsetInDateTimes() &&
      this.dateInputFormat !== "" &&
      this.timeInputFormat !== ""
    ) {
      return `${this.inputFormatWithoutOffset} ${DATETIME_OFFSET_FORMAT}`;
    }
    return this.inputFormatWithoutOffset;
  }

  /**
   */
  extractDateInputFormat(): string {
    const dateEraChars = new Set("yMLdQDEewGu");
    const timeOffsetChars = new Set("HhaKmsSZzXxO");

    const format = this.format;
    let inputFormat = "";

    let isInQuotes = false;
    let keep = false;

    for (let i = 0; i < format.length; i++) {
      const prevChar = i > 0 ? format[i - 1] : "";
      const nextChar = i < format.length ? format[i + 1] : "";
      const char = format[i];

      if (char === "'") {
        isInQuotes = prevChar === "'" || nextChar === "'" || !isInQuotes;
      } else if (!isInQuotes && dateEraChars.has(char)) {
        keep = true;
      } else if (!isInQuotes && timeOffsetChars.has(char)) {
        keep = false;
      }

      if (keep) {
        inputFormat += char;
      }
    }

    return inputFormat.trim();
  }

  /**
   */
  get dateInputFormat(): string {
    if (!this.hasDate) {
      return "";
    }

    if (!this._dateInputFormat) {
      this._dateInputFormat = this.extractDateInputFormat();
    }

    return this._dateInputFormat;
  }

  /**
   */
  get dateReadonlyFormat(): string {
    if (!this.hasDate) {
      return "";
    }

    return this.hasTime
      ? this.format.replace(this.timeInputFormat, "").trim()
      : this.format;
  }

  /**
   * return attribute placeholder or return formatlabel without time input format from contributions
   */
  get placeholder(): string {
    if (this._placeholder !== "") {
      return this._placeholder;
    }

    const formatLabel = this.getContribution("formatlabel", "");

    if (this.hasDate && this.hasTime && formatLabel.includes(" ")) {
      // return first part of formatlabel to keep placeholder matching date input
      return formatLabel.split(" ")[0];
    }

    return formatLabel;
  }

  /**
   * Set placeholder text
   */
  set placeholder(placeholder: string) {
    this._placeholder = placeholder;
  }

  /**
   */
  get timeInputFormat(): string {
    if (!this.hasTime) {
      return "";
    }

    if (this._timeInputFormat) {
      return this._timeInputFormat;
    }

    const format = this.format;

    let timeFormatParts = [];
    if (format.includes("H")) {
      timeFormatParts.push("HH");
    } else if (format.includes("h")) {
      timeFormatParts.push("hh");
    }
    if (format.includes("m")) {
      timeFormatParts.push("mm");
    }
    if (format.includes("s")) {
      timeFormatParts.push("ss");
    }

    let timeFormat = timeFormatParts.join(":");
    if (format.includes("S")) {
      timeFormat = `${timeFormat}.SSS`;
    }
    if (format.includes("a")) {
      timeFormat = `${timeFormat} a`;
    }

    this._timeInputFormat = timeFormat;
    return this._timeInputFormat;
  }

  /**
   */
  get timeReadonlyFormat(): string {
    if (!this.hasTime) {
      return "";
    }

    return this.timeInputFormat;
  }

  /**
   */
  get dateInputValue(): string {
    return typeof this.value === "string" && this.dateInputFormat !== ""
      ? this.formatUtil.toFormat(this.value, this.dateInputFormat)
      : "";
  }

  /**
   */
  get timeInputValue(): string {
    return typeof this.value === "string" && this.timeInputFormat !== ""
      ? this.formatUtil.toFormat(this.value, this.timeInputFormat)
      : "";
  }

  /**
   * format value in readonly rendering
   */
  formatValue(value: ?string): string {
    if (value == null || value.toString() === "") {
      return "";
    }

    // when the value is exactly an iso date, render as date
    if (
      (this.type === "datetime" || this.type === "timestamp") &&
      DateUtil.hasFormat(value, ISO_DATE_FORMAT)
    ) {
      return DateUtil.toFormat(value, this.dateReadonlyFormat);
    }

    return this.formatUtil.toFormat(value, this.format);
  }

  /**
   * Retrieve readonly value, the date in the configurated format
   */
  get readonlyvalue(): string {
    if (typeof this.value === "string") {
      return this.formatValue(this.value.toString());
    }

    return "";
  }

  /**
   */
  get isAmbiguous(): boolean {
    if (typeof this.value === "string") {
      return this.formatUtil.isAmbiguous(this.value);
    }
    return false;
  }

  /**
   */
  get offset(): OffsetInfo | null {
    if (typeof this.value === "string") {
      return this.formatUtil.getOffset(this.value);
    }
    return null;
  }

  /**
   * Get minimum date
   */
  get mindate(): string | null {
    return this.getContribution("mindate");
  }

  /**
   * Get maximum date
   */
  get maxdate(): string | null {
    return this.getContribution("maxdate");
  }

  /**
   * Registers an error that was received from a server response
   */
  addServerError(error: FormErrorAnchor) {
    const { id, message, properties, layouthint } = error;
    if (properties && has(properties, "format") && this.formatLabel) {
      properties.format = this.formatLabel;
    }

    this._errorCollection.addServerError(id, message, properties, layouthint);
  }

  /**
   */
  getFormatConstraint(): IConstraintModel {
    switch (this.type) {
      case "date":
        return new DateTimeDateFormatConstraint(
          this.dateInputFormat,
          this.formatLabel,
        );
      case "time":
        return new DateTimeTimeFormatConstraint(
          this.timeInputFormat,
          this.formatLabel,
        );
      default:
        return new DatetimeFormatConstraint(
          this.type,
          this.inputFormat,
          this.formatLabel,
        );
    }
  }

  /**
   * Add Date constraints for attribute
   */
  addConstraints(): ConstraintCollection {
    const constraints = new ConstraintCollection();

    constraints.add(this.getFormatConstraint());

    if (this.mindate || this.maxdate) {
      constraints.add(
        new DateBoundaryConstraint(
          this.type,
          this.mindate,
          this.maxdate,
          this.inputFormat,
        ),
      );
    }

    return constraints;
  }

  /**
   */
  get readonlyWidth(): $Keys<typeof ATTRIBUTE_WIDTH> {
    if (this.type === "date" || this.type === "time") {
      return ATTRIBUTE_WIDTH.SMALL;
    }

    return ATTRIBUTE_WIDTH.MEDIUM;
  }

  /**
   */
  updateByAttribute(otherAttribute: AttributeType): void {
    this.reset();
    if (otherAttribute instanceof DatetimeAttributeModel) {
      const otherValue = otherAttribute.value;
      if (otherValue != null) {
        const inputValue = this.formatUtil.toFormat(
          otherValue,
          this.inputFormat,
        );
        this.update(inputValue);
      }
    } else {
      console.error(
        `update of datetime attribute with ${otherAttribute.type} not supported`,
      );
    }
  }
}

export default DatetimeAttributeModel;
